import {
  BusinessEntity,
  CountryCode,
  CurrencyCode,
  ServerLocation,
} from './countries'
import { SearchParams } from './pagination'
import { Partner, PartnerRecord } from '~/types/partners'
import { TranslationArgs } from '~/types/translation'
import {
  DiscountCategory,
  DiscountType,
  ProductLine,
  SubscriptionEstimatePayload,
  SubscriptionsEstimateDiscount,
  SubscriptionsEstimateResponse,
} from './prices'
import { SalesforceCall, SalesforceStatus } from '~/types/admin'
import { ApiVersion } from './version'

type Customer = {
  id: string
  name: string
  country: keyof typeof CountryCode
  city: string
  street: string
  postalCode: string
  state: string
  contactEmail: string
}

export enum AddonType {
  CLOUD_DATABASE = 'cloudDatabase',
  PRINT_HISTORY_RETENTION = 'printHistoryRetention',
  SUPPORT_LEVEL = 'supportLevel',
  CLOUD_PRINTING_INTEGRATION = 'cloudPrintingIntegration',
  NON_PRODUCTION_ENVIRONMENT = 'nonProductionEnvironment',
  SAP_INTEGRATION = 'sapIntegration',
  NICELABEL_ACADEMY_UNLIMITED = 'nicelabelAcademyUnlimited',
  ORACLE_INTEGRATION = 'oracleIntegration',
  CLOUD_AUTOMATION_SERVICE = 'cloudAutomationService',
  HYBRID_DEPLOYMENT = 'hybridDeployment',
  OFFLINE_LICENSE_KEY = 'offlineLicenseKey',
}

export enum ProductType {
  CLOUD = 'cloud',
}

export enum Plan {
  DESIGNER = 'DESIGNER',
  ESSENTIALS = 'ESSENTIALS',
  BUSINESS = 'BUSINESS',
  DESIGNER_PRO = 'DESIGNER_PRO',
  LMS_ENTERPRISE = 'LMS_ENTERPRISE',
  LMS_PRO = 'LMS_PRO',
  POWERFORMS_SUITE = 'POWERFORMS_SUITE',
}

export enum Env {
  PRODUCTION = 'Prod',
  SANDBOX = 'Sandbox',
}

export enum Status {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  TRIAL = 'TRIAL',
  CANCELLED = 'CANCELLED',
  ON_HOLD = 'ON_HOLD',
}

export enum BillingPeriod {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
  YEARS_3 = 'YEARS_3',
}

export enum PaymentMethod {
  Card = 'CARD',
  PayPal = 'PAYPAL',
  Offline = 'OFFLINE',
}

export enum PaymentSourceType {
  EXISTING_CARD = 'EXISTING_CARD',
  NEW_CARD = 'NEW_CARD',
  EXISTING_PAYPAL = 'EXISTING_PAYPAL',
  NEW_PAYPAL = 'NEW_PAYPAL',
  WIRE_TRANSFER = 'WIRE_TRANSFER',
}

export type PaymentDetails = {
  id: string
  type: string
  brand: string
  cardLast4: string
  expiryMonth: string
  expiryYear: string
  email: string
}

export enum RenewalCouponStatus {
  PENDING = 'PENDING',
  APPLIED = 'APPLIED',
  USED = 'USED',
  DELETED = 'DELETED',
}

export enum RenewalCouponDiscountType {
  PERCENTAGE = 'PERCENTAGE',
  FIXED_AMOUNT = 'FIXED_AMOUNT',
}

export type RenewalCoupon = {
  id: string
  discountType: RenewalCouponDiscountType
  discountValue: number
  status: RenewalCouponStatus
  chargebeeCouponId: string
  subscriptionId: string
}

// Subscription
export type Subscription = {
  notes: string
  numberOfPrinters: number
  billingPeriod: BillingPeriod
  billingAmount: number
  arr: number
  mrr: number
  currencyCode: CurrencyCode
  customer: Customer | null
  partner: Partner | null
  id: string
  status: Status
  addons: { type: SubscriptionAddonType; [key: string]: string }[]
  subscriptionName: string
  plan: Plan
  chargebeeSubscriptionId: string
  serverLocation: ServerLocation
  termEnd: string
  termStart: string
  trialStart: string
  trialEnd: string
  nicelabelAccountId: string
  accountOwnerEmail: string
  productOrderNumber: string
  endTerm: string
  trialExtended: boolean
  issuesCreditNoteOnCancellation: boolean
  renewable: boolean
  instances: NiceLabelInstance[]
  productLine: ProductLine
  salesforceCallFailed?: boolean
  salesforceCall?: SalesforceCall
  reseller?: Partial<Partner>
  createdAt?: string
  createdBy?: PartnerRecord
  updatedBy?: PartnerRecord
  deleteScheduledOn?: string
  opportunityId?: string
  updatedPoNumberAt: string | null
  hasVoucher?: boolean
  updatedAt?: string
  discounts: SubscriptionsEstimateDiscount[]
  renewalCoupons?: RenewalCoupon[]
  businessEntity: BusinessEntity
}

export type SubscriptionPriceType = {
  serverLocation: ServerLocation
  currencySign: string
  currencyCode: CurrencyCode
  recommendedPrice: number
  partnerPrice: number
  totalExclTax: number
  taxPercentage: number
  taxAmount: number
  period: BillingPeriod
  discounts: SubscriptionsEstimateDiscount[]
}
export type SubscriptionsResponse = {
  data: Subscription[]
  pages: number
  total: number
}

export type NiceLabelInstance = {
  id: string
  subscriptionPlan: string
  name: string
  type: InstanceType
  nicelabelCustomerId: string
  accessLink: string
  serverLocation: string
  inviteEmails: string
  isProvisioned: boolean
  licenseKey: string | null
  apiVersion: ApiVersion
}

export type NiceLabelInstancesResponse = NiceLabelInstance[]

export type NiceLabelInstanceRow = Omit<NiceLabelInstance, 'type'> & {
  type: string
}

export interface SubscriptionsSearchParams extends SearchParams {
  customerId?: string
  partnerId?: string
  status?: string
  billingPeriod?: string
  subscriptionName?: string
  trialCancelled?: boolean
  includeDeleted?: boolean
}

export interface CreateSubscriptionArgs {
  flowId: string
  serverLocation: ServerLocation
  billingPeriod: BillingPeriod
  payment_source_id?: string
  customerId: string
  numberOfPrinters: string
  partnerId?: string
  accountOwnerEmail: string
  productOrderNumber?: string
  notes?: string
  addons: SubscriptionAddons
  subscriptionPlan: string
  environment: EnvironmentInstance
  renewable: boolean
  couponId?: string
}

export interface CancelSubscriptionArgs {
  deleteInstances: boolean
  partialCreditNote: boolean
}

export type ColumnNames = Map<
  string,
  {
    translation: TranslationArgs
    sorting?: string
    width?: number
  }
>

export type SubscriptionAddons = {
  [key: string]: { [key: string]: string | null }
}

export type EnvironmentInstance = {
  [key: string]: { name: string; urlPrefix: string; inviteEmails: string }
}

export type EnvironmentProps = {
  uniqueUrlPrefix: string
  instanceName: string
}

export interface Environment {
  prodEnv: EnvironmentProps
  sandboxEnv: EnvironmentProps
}

export interface Access {
  environment: Environment
  accessEmailDomain: string
}

export enum EnvironmentType {
  PRODUCTION = 'prodEnv',
  SANDBOX = 'sandboxEnv',
}

export enum EnvironmentProperties {
  UNIQUE_URL_PREFIX = 'uniqueUrlPrefix',
  INSTANCE_NAME = 'instanceName',
}

export interface UpdateEnvironment {
  input: string
  type: EnvironmentType
  property: EnvironmentProperties
}

export type HighlightSection = {
  body?: string
  title?: string
  icon?: string
  background?: boolean
}

export type MoreInformation = {
  title?: string
  body: string
  link?: string
  highlightSection?: HighlightSection
}

export enum InstanceType {
  PRODUCTION = 'PRODUCTION',
  SANDBOX = 'SANDBOX',
}

export enum SubscriptionAddonType {
  CLOUD_DATABASE = 'CLOUD_DATABASE',
  PRINT_HISTORY_RETENTION = 'PRINT_HISTORY_RETENTION',
  SUPPORT_LEVEL = 'SUPPORT_LEVEL',
  CLOUD_PRINTING_INTEGRATION = 'CLOUD_PRINTING_INTEGRATION',
  NON_PRODUCTION_ENVIRONMENT = 'NON_PRODUCTION_ENVIRONMENT',
  SAP_INTEGRATION = 'SAP_INTEGRATION',
  ORACLE_INTEGRATION = 'ORACLE_INTEGRATION',
  HYBRID_DEPLOYMENT = 'HYBRID_DEPLOYMENT',
  NICELABEL_ACADEMY_UNLIMITED = 'NICELABEL_ACADEMY_UNLIMITED',
  OFFLINE_LICENSE_KEY = 'OFFLINE_LICENSE_KEY',
  CLOUD_AUTOMATION_SERVICE = 'CLOUD_AUTOMATION_SERVICE',
}

export enum SupportLevel {
  BASIC = 'BASIC',
  STANDARD = 'STANDARD',
  PLATINUM = 'PLATINUM',
}

export enum StorageSize {
  SIZE_1_GB = 'SIZE_1_GB',
  SIZE_5_GB = 'SIZE_5_GB',
  SIZE_100_GB = 'SIZE_100_GB',
  SIZE_250_GB = 'SIZE_250_GB',
}

export enum RetentionDuration {
  DURATION_3_MONTHS = 'DURATION_3_MONTHS',
  DURATION_1_YEARS = 'DURATION_1_YEARS',
  DURATION_5_YEARS = 'DURATION_5_YEARS',
}

export enum CloudAutomationServiceInstanceType {
  BASIC = 'BASIC',
  STANDARD = 'STANDARD',
}

export enum AddonConfigurationType {
  NEW_SUBSCRIPTION = 'NEW_SUBSCRIPTION',
  CHANGE_SUBSCRIPTION = 'CHANGE_SUBSCRIPTION',
}

type AddonConfiguration = {
  disabledMessage?: {
    [key in AddonConfigurationType]?: {
      message: string
      showMessage: boolean
    }
  }
}

export interface Addon {
  productLine?: ProductLine[]
  title: string
  desc: string
  value: string
  moreInformation?: MoreInformation
  addOnValue: string | null
  options: {
    [key: string]: {
      title: string
      compactTitle?: string
      desc: string | null
      value: string | string[]
      chargebeeId?: string
      options?: { [key: string]: Option }
    }
  }
  hiddenInTrialMode?: boolean
  new?: boolean
  configuration?: AddonConfiguration
}

export interface Addons {
  [key: string]: Addon
}

interface Option {
  label: number
  value: string
  options?: { [key: string]: Option }
}

export interface Product {
  id: string
  label: string
  logo: string
  value: string
}

export interface Products {
  [key: string]: Product
}

export type CheckEmailDomainResponse = {
  formatValid: boolean
  domainAvailable: boolean
}

export type checkUniqueInstanceName = {
  isUnique: boolean
}

export enum BillingTableType {
  INVOICES = 'invoices',
  CREDIT_NOTES = 'credit-notes',
}

export type UpcomingRenewal = {
  id: string
  termEnd: string
}

export interface DownloadCsvSearchParams extends SearchParams {
  customerId?: string
  partnerId?: string
  subscriptionName?: string
  billingPeriod?: string
  status?: string
}

export enum EditPoModalType {
  ON_HOLD = 'ON_HOLD',
  RENEWAL = 'RENEWAL',
  ACTIVATE = 'ACTIVATE',
}

export interface EditPoNumberModalState {
  type: EditPoModalType
  userAuthorization: boolean
  estimate?: SubscriptionsEstimateResponse
  showSuccessModal: boolean
  couponInputValue: string
  appliedCoupon: string
  couponWarning: string
}

export type CouponResponse = {
  id: string
  amount: number
  percentage: number
  discountCategory: DiscountCategory
  discountType: DiscountType
}

export type ValidateCouponRequest = {
  couponId: string
  partnerId: string
  estimateRequest: SubscriptionEstimatePayload
}

export type LocalSubscriptionData = {
  productOrderNumber: string
  accountOwnerEmail: string
  subscriptionName: string
  renewable: boolean
}

export enum SubscriptionOrderBy {
  CREATED_AT = 'CREATED_AT',
  PARTNER_ID = 'PARTNER_ID',
  STATUS = 'STATUS',
  CUSTOMER_ID = 'CUSTOMER_ID',
  SUBSCRIPTION_NAME = 'SUBSCRIPTION_NAME',
  INSTANCE_NAME = 'INSTANCE_NAME',
  BILLING_PERIOD = 'BILLING_PERIOD',
  RESELLER_ID = 'RESELLER_ID',
  INCLUDE_DELETED = 'INCLUDE_DELETED',
  TRIAL_CANCELLED = 'TRIAL_CANCELLED',
  CUSTOMER_COUNTRY = 'CUSTOMER_COUNTRY',
  CUSTOMER_POSTAL_CODE = 'CUSTOMER_POSTAL_CODE',
  PARTNER_NAME = 'PARTNER_NAME',
  CUSTOMER_NAME = 'CUSTOMER_NAME',
  PLAN = 'PLAN',
  NUMBER_OF_PRINTERS = 'NUMBER_OF_PRINTERS',
  NOTES = 'NOTES',
  PRODUCT_ORDER_NUMBER = 'PRODUCT_ORDER_NUMBER',
  PARTNER_COUNTRY = 'PARTNER_COUNTRY',
  EXPIRY_DAYS = 'EXPIRY_DAYS',
  TERM_END = 'TERM_END',
  SALESFORCE_CALL_FAILED = 'SALESFORCE_CALL_FAILED',
  EXPIRY = 'EXPIRY',
  PRINTER_SEATS = 'PRINTER_SEATS',
  PAYMENT = 'PAYMENT',
  PRODUCT = 'PRODUCT',
  RESELLER_NAME = 'RESELLER_NAME',
  CHARGEBEE_SUBSCRIPTION_ID = 'CHARGEBEE_SUBSCRIPTION_ID',
  OPPORTUNITY_ID = 'OPPORTUNITY_ID',
  PROVIDERS_FAILED = 'PROVIDERS_FAILED',
  AUTO_INVOICE = 'AUTO_INVOICE',
}

export enum UpgradePrinterSeatsStep {
  SETUP = 0,
  PAYMENT = 1,
}

export interface SubscriptionRow {
  notes: string
  customerId?: string
  subscriptionId: string
  customerName: string
  partnerName: string
  resellerName: string
  subscriptionName: string
  termEnd?: string
  product: string
  numberOfPrinters: number
  status: Status
  daysLeft: number
  email: string
  payment: BillingPeriod
  salesforceCallFailed: SalesforceStatus
  instanceName: string
  renewable: boolean
  businessEntity: Subscription['businessEntity']
}

export interface PaypalCallbackArgs extends CreateSubscriptionArgs {
  customer: any
  contacts: { email: string; firstName: string; lastName: string }
  partner: Partial<Partner>
  nonce: string
}

export type SpecialSyncVariant = 'pink' | 'purple'

export interface SpecialSyncConfig {
  variant: SpecialSyncVariant
  emojis: string[]
  toastStyle: Record<string, string>
}

export interface SubscriptionFields {
  productOrderNumber: string
  accountOwnerEmail: string
  subscriptionName: string
}

export enum SubscriptionSteps {
  RESELLER = 'reseller',
  CUSTOMER = 'customer',
  PRODUCT = 'product',
  CONFIGURATION = 'configuration',
  ACCESS = 'access',
  PAYMENT = 'payment',
}

export enum ChangeSubscriptionSteps {
  SETUP = 'setup',
  CONFIGURATION = 'configuration',
  ACCESS = 'access',
  PAYMENT = 'payment',
}
